import React, { useEffect, useState, useCallback, useRef } from "react";
import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import { Box, Stack, Typography, Button, Tooltip } from "@mui/material";
import { SavedPlotFeedGrid } from "./SavedPlotFeedGrid";
import { type SavedPlotRow, type SavedMapPlotRequestDetail } from "../types";
import { type GridSelectionModel } from "@mui/x-data-grid";
import { useMapPlotRequest } from "@/features/map/hooks/useMapPlotRequest";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onPlotSelected?: (plotDetails: SavedMapPlotRequestDetail[]) => void;
}

const MAX_PLOT_COUNT = 1_000;

export const SavedPlotModal: React.FC<IProps> = (props) => {
  const { open, setOpen, onPlotSelected } = props;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const { refetch, fetchPlotDetails, isError } = useMapPlotRequest(open);
  const [rows, setRows] = useState<SavedPlotRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  // データがすでに取得されているかを追跡するref
  const dataFetchedRef = useRef(false);

  const handleRowSelectionChange = (
    newSelectionModel: GridSelectionModel
  ): void => {
    setSelectedIds(newSelectionModel);
  };

  // 強制的に再取得するためのフラグ
  const [forceRefresh, setForceRefresh] = useState(false);

  const fetchData = useCallback(async () => {
    // すでにデータが取得されている場合は何もしない
    if (dataFetchedRef.current && !forceRefresh) {
      return;
    }

    if (open) {
      setIsModalLoading(true);
      try {
        const result = await refetch();
        if (result && result.length > 0) {
          const formattedRows: SavedPlotRow[] = result.map((request) => ({
            id: request.id,
            selected: false,
            name: request.name,
            count: request.count,
            createdAt: request.createdAt,
          }));

          setRows(formattedRows);

          dataFetchedRef.current = true;
        } else {
          setRows([]);
        }
      } catch (error) {
        console.error("データ取得エラー:", error);
      } finally {
        setIsModalLoading(false);
        setForceRefresh(false);
      }
    }
  }, [open, refetch]);

  // NOTE: 更新ボタン押下時のハンドラーを独立させ、useEffectとの依存関係を切り離す
  const handleRefreshData = (): void => {
    dataFetchedRef.current = false;
    setIsModalLoading(true);
    refetch()
      .then((result) => {
        if (result && result.length > 0) {
          const formattedRows: SavedPlotRow[] = result.map((request) => ({
            id: request.id,
            selected: false,
            name: request.name,
            count: request.count,
            createdAt: request.createdAt,
          }));
          setRows(formattedRows);
          dataFetchedRef.current = true;
        } else {
          setRows([]);
        }
      })
      .catch((error) => {
        console.error("データ取得エラー:", error);
      })
      .finally(() => {
        setIsModalLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      if (!dataFetchedRef.current) {
        void fetchData();
      }
    }
  }, [open, fetchData]);

  const handlePlotToMap = async (): Promise<void> => {
    if (selectedIds.length === 0) {
      toast.error("プロットするデータを選択してください。");
      return;
    }

    setIsModalLoading(true);
    try {
      const selectedMapPlotRequestDetails = await fetchPlotDetails(selectedIds);

      if (selectedMapPlotRequestDetails.length === 0) {
        toast.error("プロット可能なデータが存在しません。");
        return;
      }

      if (selectedMapPlotRequestDetails.length > MAX_PLOT_COUNT) {
        toast.error(
          `プロット可能な最大数（${MAX_PLOT_COUNT}件）を超えています。プロットしようとしているピン数: ${selectedMapPlotRequestDetails.length}件`
        );
        return;
      }

      if (onPlotSelected) {
        onPlotSelected(selectedMapPlotRequestDetails);
      }

      setSelectedIds([]);
      setOpen(false);
    } catch (error) {
      console.error("プロット詳細の取得に失敗しました:", error);

      if (isAxiosError(error) && error.response?.status === 404) {
        toast.error("プロット可能なデータがありませんでした。");
      } else {
        toast.error("プロット詳細の取得に失敗しました。");
      }
    } finally {
      setIsModalLoading(false);
    }
  };

  const hasData = rows.length > 0;
  const showLoading = isModalLoading;
  const canPlotToMap = selectedIds.length > 0 && hasData;

  return (
    <CustomModal
      isOpen={open}
      handleClose={() => {
        setOpen(false);
      }}
      ariaLabelledby="saved-plot-info-modal"
      ariaDescribedby="saved-plot-info-modal-description"
      width={"80%"}
      height={"80%"}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <PageTitle>保存されたプロット情報を呼び出す</PageTitle>
        <Tooltip title="データを再取得する">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshData}
            disabled={showLoading}
          >
            更新
          </Button>
        </Tooltip>
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "80%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {showLoading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ my: "20px" }} />
            </Stack>
          ) : isError ? (
            <Stack alignItems="center">
              <Typography color="error" sx={{ my: "20px" }}>
                データの取得に失敗しました。
              </Typography>
            </Stack>
          ) : (
            <SavedPlotFeedGrid
              handleRowSelectionChange={handleRowSelectionChange}
              rows={rows}
              selectedIds={selectedIds}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          )}
        </Box>
        <Box
          sx={{
            mt: 2,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            ※ プロット可能なデータは最大で1,000件までです
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            ※
            データの登録はバックグラウンドで処理されます。処理完了後にメールでお知らせが届きましたら、データを取得できます
          </Typography>
        </Box>
        <Stack direction="row" justifyContent="end" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              void handlePlotToMap();
            }}
            disabled={!canPlotToMap}
          >
            地図にプロットする
          </Button>
        </Stack>
      </Box>
    </CustomModal>
  );
};
